<!-- src/views/OnBoarding/AnnualHealthAssessmentFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">Annual Health Assessment</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Occupational Health & Fitness Review • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Medical Review</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">

          <!-- Dynamic Introductory Text -->
          <section 
            v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" 
            class="mb-12 p-8 bg-slate-50 rounded-3xl border border-slate-200 shadow-inner"
          >
            <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2">
               <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
               <span class="font-black uppercase text-xs">Loading Instructions...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-700" v-html="mainTextContent"></div>
          </section>

          <form @submit.prevent="submitForm" class="space-y-16">
            
            <!-- Section 1: Your Details -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Your Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">First Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.firstName || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Last Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.lastName || 'Loading...' }}</p>
                </div>
                <div class="md:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Verified Email</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
              </div>
            </section>

            <!-- Section 2: Occupational History -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <ClockIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Occupational History</h2>
              </div>

              <div class="space-y-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <QuestionYesNo id="terminatedIllHealth" label="Has your employment ever been terminated on the grounds of ill health?" v-model="formData.terminatedIllHealth" v-model:details="formData.terminatedIllHealthDetails" details-label="If yes, please provide details, including medication taken"/>
                <QuestionYesNo id="medicalInvestigations" label="Are you currently undergoing any medical investigations?" v-model="formData.medicalInvestigations" v-model:details="formData.medicalInvestigationsDetails" details-label="If yes, please provide details"/>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t border-slate-200">
                    <div>
                        <label for="sicknessDaysLast12Months" class="form-label">Absence (Last 12 Months)</label>
                        <input id="sicknessDaysLast12Months" v-model.number="formData.sicknessDaysLast12Months" type="number" class="form-input" placeholder="Number of days">
                    </div>
                    <div>
                        <label for="sicknessDaysPrior12Months" class="form-label">Absence (Prior 12 Months)</label>
                        <input id="sicknessDaysPrior12Months" v-model.number="formData.sicknessDaysPrior12Months" type="number" class="form-input" placeholder="Number of days">
                    </div>
                </div>

                <div class="pt-6 border-t border-slate-200">
                    <QuestionYesNo id="terminatedTWAct" label="Has your employment ever been terminated under Transport and Works Act 1992 (Drugs & Alcohol)?" v-model="formData.terminatedTWAct" v-model:details="formData.terminatedTWActDetails" details-label="If yes, please provide details"/>
                </div>
              </div>
            </section>

            <!-- Section 3: Medical Condition -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <HeartIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Medical Condition</h2>
              </div>

              <div class="space-y-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                    <QuestionYesNo id="disabilities" label="a. Do you consider yourself to have any physical or mental disabilities?" v-model="formData.disabilities" v-model:details="formData.disabilityDetails" details-label="a. Nature of disability and adverse effects:"/>
                    <QuestionYesNo id="jointProblems" label="b. Health problems affecting joints (Back, knees, shoulders, etc.) in last 2 years?" v-model="formData.jointProblems" v-model:details="formData.jointProblemDetails" details-label="b. Details (Medication/Time off):"/>
                    <QuestionYesNo id="diabetes" label="c. Do you have diabetes, requiring insulin?" v-model="formData.diabetes" />
                    <QuestionYesNo id="epilepsy" label="d. Do you suffer from epilepsy or fits?" v-model="formData.epilepsy" />
                    <QuestionYesNo id="blackouts" label="e. Blackouts, recurrent dizziness or sudden collapse?" v-model="formData.blackouts" />
                    <QuestionYesNo id="chestPain" label="f. Discomfort/pain in chest or shortness of breath on exercise?" v-model="formData.chestPain" />
                    <QuestionYesNo id="mobilityDifficulty" label="g. Difficulty moving rapidly over slopes, steps or rough ground?" v-model="formData.mobilityDifficulty" />
                    <QuestionYesNo id="shoulderDifficulty" label="h. Difficulty in looking over your shoulder?" v-model="formData.shoulderDifficulty" />
                    <QuestionYesNo id="eyesightDifficulty" label="i. Any eyesight difficulties (other than corrective lenses)?" v-model="formData.eyesightDifficulty" />
                    <QuestionYesNo id="hearingDifficulty" label="j. Difficulty in hearing normal conversation?" v-model="formData.hearingDifficulty" />
                    <QuestionYesNo id="drowsinessMedication" label="k. Taking medication causing dizziness or drowsiness?" v-model="formData.drowsinessMedication" />
                    <QuestionYesNo id="alcoholIllness" label="l. Illness related to alcohol during the last 12 months?" v-model="formData.alcoholIllness" />
                    <QuestionYesNo id="vibratingTools" label="m. Usage of hand-held vibrating tools since last assessment?" v-model="formData.vibratingTools" v-model:details="formData.vibratingToolsDetails" details-label="m. Usage details:"/>
                    <QuestionYesNo id="fingerNumbness" label="n. Numbness/tingling (>20 mins) after using vibration equipment?" v-model="formData.fingerNumbness" />
                    <QuestionYesNo id="fingerNumbnessOther" label="o. Numbness or tingling of the fingers at any other time?" v-model="formData.fingerNumbnessOther" />
                    <QuestionYesNo id="wakeWithPain" label="p. Wake at night with pain, tingling, or numbness in hand/wrist?" v-model="formData.wakeWithPain" />
                    <QuestionYesNo id="fingersGoneWhite" label="q. Have any of your fingers gone white on cold exposure?" v-model="formData.fingersGoneWhite" />
                    <QuestionYesNo id="coldToleranceChange" label="r. Noticed any change in response to working outdoors in the cold?" v-model="formData.coldToleranceChange" />
                    <QuestionYesNo id="handArmProblems" label="s. Are you experiencing any other problems in your hands or arms?" v-model="formData.handArmProblems" />
                    <QuestionYesNo id="difficultySmallObjects" label="t. Difficulty picking up small objects or opening tight jars?" v-model="formData.difficultySmallObjects" />
                    <QuestionYesNo id="healthChange" label="u. Has anything changed about your health since last assessment?" v-model="formData.healthChange" v-model:details="formData.healthChangeDetails" details-label="u. Details:"/>
                    
                    <div class="pt-6 border-t border-slate-200">
                        <QuestionYesNo 
                            id="symptomsControlled" 
                            label="v. Are any symptoms you currently have well controlled?" 
                            v-model="formData.symptomsControlled" 
                            v-model:details="formData.symptomsControlledDetails" 
                            details-label="v. If no, please provide details. *" 
                            is-inverted-details-logic
                        />
                        <div v-if="formData.symptomsControlled === true" class="mt-6 p-6 bg-[#EBFDF8] border border-[#20FCF1]/30 rounded-2xl animate-fadeIn">
                            <div class="flex items-start">
                                <input 
                                    id="symptomsControlledConfirmation" 
                                    type="checkbox" 
                                    v-model="formData.symptomsControlledConfirmation" 
                                    class="h-6 w-6 text-[#0A1630] border-slate-300 rounded-lg focus:ring-[#20FCF1] mt-0.5"
                                >
                                <label for="symptomsControlledConfirmation" class="ml-4 block text-sm font-black text-[#0A1630] uppercase tracking-tight">
                                    I confirm that I am symptom free and fit to work. <span class="text-red-500">*</span>
                                </label>
                            </div>
                        </div>
                    </div>
              </div>
            </section>

            <!-- Section 4: Additional Information -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <DocumentPlusIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Additional Information</h2>
              </div>
              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <label for="otherConditions" class="form-label">Historical or existing condition(s) pertinent to declare:</label>
                <textarea id="otherConditions" v-model="formData.otherConditions" rows="4" class="form-input mt-2" placeholder="Provide any additional context here..."></textarea>
              </div>
            </section>

            <!-- Section 5: Declaration -->
            <section class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
              <div class="absolute top-0 right-0 p-10 opacity-5">
                <ShieldCheckIcon class="h-32 w-32" />
              </div>
              <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                  <div class="max-w-2xl">
                    <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-4">5. Declaration</h2>
                    <p class="text-slate-400 font-medium text-sm leading-relaxed">
                        I declare that all information is true and no material facts have been withheld. I agree to the processing of sensitive data in accordance with the Data Protection Act.
                    </p>
                  </div>
                  <div class="bg-white/5 p-6 rounded-[32px] border border-white/10">
                      <div class="flex items-center">
                          <input id="declaration" type="checkbox" v-model="formData.declaration" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent">
                          <label for="declaration" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight">Accept &<br>Certify <span class="text-red-500">*</span></label>
                      </div>
                  </div>
              </div>
            </section>

            <!-- Section 6: Signature Preview -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">6. Verification Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto">
                    <label class="form-label text-slate-400 text-center mb-6">Profile Linked Digital Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Identity Verified</span>
                            </div>
                        </div>
                        <div v-else class="text-center space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-black uppercase tracking-tight border border-red-100">
                                Profile Signature Missing
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest">
                                Complete Profile
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <div class="pt-10">
              <button type="submit" :disabled="!isFormValid || isSubmitting" class="btn-premium">
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Processing Assessment...
                </span>
                <span v-else>Submit Annual Health Assessment</span>
              </button>
               <p v-if="!isFormValid && showValidationMessage" class="mt-6 text-center text-red-600 font-bold uppercase text-xs tracking-widest animate-pulse">
                Please complete all required fields before submission
               </p>
            </div>
          </form>

          <div v-if="successMessage" class="mt-10 p-8 bg-mint-50 border border-brand-accent rounded-[32px] text-center shadow-lg animate-bounce">
            <p class="text-brand-primary font-black uppercase tracking-widest">{{ successMessage }}</p>
          </div>
          <div v-if="errorMessage" class="mt-10 p-8 bg-red-50 border border-red-200 rounded-[32px] text-center font-bold text-red-700">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';
import QuestionYesNo from '@/components/forms/QuestionYesNo.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  ClockIcon, 
  HeartIcon, 
  DocumentPlusIcon, 
  ShieldCheckIcon, 
  PencilSquareIcon,
  CheckBadgeIcon
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ firstName: '', lastName: '', email: '', signatureUrl: '', signatureDataUrl: '' });
const formData = reactive({
  terminatedIllHealth: null, terminatedIllHealthDetails: '',
  medicalInvestigations: null, medicalInvestigationsDetails: '',
  sicknessDaysLast12Months: null, sicknessDaysPrior12Months: null,
  terminatedTWAct: null, terminatedTWActDetails: '',
  disabilities: null, disabilityDetails: '',
  jointProblems: null, jointProblemDetails: '',
  diabetes: null, epilepsy: null, blackouts: null, chestPain: null, mobilityDifficulty: null, shoulderDifficulty: null,
  eyesightDifficulty: null, hearingDifficulty: null, drowsinessMedication: null, alcoholIllness: null,
  vibratingTools: null, vibratingToolsDetails: '', fingerNumbness: null, fingerNumbnessOther: null, wakeWithPain: null,
  fingersGoneWhite: null, coldToleranceChange: null, handArmProblems: null, difficultySmallObjects: null,
  healthChange: null, healthChangeDetails: '',
  symptomsControlled: null, symptomsControlledDetails: '',
  symptomsControlledConfirmation: false, 
  otherConditions: '', declaration: false,
});

const mainTextContent = ref('');
const isLoadingText = ref(true);
const isSubmitting = ref(false);
const showValidationMessage = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const isFormValid = computed(() => {
    if (isLoadingText.value) return false;
    if (!formData.declaration) return false;
    if (formData.sicknessDaysLast12Months === null || formData.sicknessDaysLast12Months === '') return false;
    if (formData.sicknessDaysPrior12Months === null || formData.sicknessDaysPrior12Months === '') return false;

    if (formData.symptomsControlled === true && !formData.symptomsControlledConfirmation) return false;
    if (formData.symptomsControlled === false && (!formData.symptomsControlledDetails || formData.symptomsControlledDetails.trim() === '')) return false;

    const requiredQuestions = [
        'terminatedIllHealth', 'medicalInvestigations', 'terminatedTWAct',
        'disabilities', 'jointProblems', 'diabetes', 'epilepsy', 'blackouts', 
        'chestPain', 'mobilityDifficulty', 'shoulderDifficulty', 'eyesightDifficulty', 
        'hearingDifficulty', 'drowsinessMedication', 'alcoholIllness', 'vibratingTools', 
        'fingerNumbness', 'fingerNumbnessOther', 'wakeWithPain', 'fingersGoneWhite', 
        'coldToleranceChange', 'handArmProblems', 'difficultySmallObjects', 
        'healthChange', 'symptomsControlled'
    ];

    for (const key of requiredQuestions) {
        if (formData[key] === null) return false;
    }
    return true;
});

const fetchPrerequisites = async () => {
    if (!user.value) {
        errorMessage.value = "Authentication not found.";
        isLoadingText.value = false;
        return;
    }
    try {
        isLoadingText.value = true;
        const idTokenResult = await user.value.getIdTokenResult();
        const clientId = idTokenResult.claims.clientId;
        if (!clientId) throw new Error("Client ID not found.");

        const [profileSnap, clientSnap] = await Promise.all([
            getDoc(doc(db, 'userProfiles', user.value.uid)),
            getDoc(doc(db, 'clients', clientId))
        ]);

        if (profileSnap.exists()) {
            const profile = profileSnap.data();
            userProfile.firstName = profile.firstName || '';
            userProfile.lastName = profile.lastName || '';
            userProfile.email = profile.email || user.value.email;
            userProfile.signatureUrl = profile.signatureUrl || '';
            userProfile.signatureDataUrl = profile.signatureDataUrl || '';
        } else {
            throw new Error("Complete 'Your Details' first.");
        }

        if (clientSnap.exists()) {
            mainTextContent.value = clientSnap.data().onboardingFormTexts?.annualHealthAssessment || '';
        }
    } catch (error) {
        errorMessage.value = `Error loading form data: ${error.message}`;
    } finally {
        isLoadingText.value = false;
    }
};

onMounted(fetchPrerequisites);

const submitForm = async () => {
  showValidationMessage.value = true;
  if (!isFormValid.value) return;
  
  isSubmitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';

  const submissionData = {
    ...formData,
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    email: userProfile.email,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    introductoryText: mainTextContent.value,
    status: 'completed',
  };

  try {
    const submitFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFunction({
      formIdentifier: 'annualHealthAssessment',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });

    if (result.data.success) {
        successMessage.value = `Assessment submitted successfully! Reference: ${result.data.referenceNumber}`;
        setTimeout(() => router.push({ name: 'onboardingForms' }), 2000);
    } else throw new Error(result.data.error || 'Submission failed.');
  } catch (error) {
    errorMessage.value = `Failed: ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm; 
}
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center; 
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  margin-bottom: 1.25rem;
}
:deep(.ql-editor h2) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>